/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Alert } from 'sarsaparilla';

export type VerifyEmailErrorType =
    | 'additionalChallenge'
    | 'recaptchaFailed'
    | 'invalidCode'
    | 'lockout'
    | 'unknownEmail'
    | 'unknown'
    | null;

interface VerifyEmailErrorsProps {
    errorType: VerifyEmailErrorType;
    className?: string;
}

export function VerifyEmailErrors({ errorType, className }: VerifyEmailErrorsProps) {
    if (!errorType) {
        return null;
    }

    if (errorType === 'additionalChallenge') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Additional Verification Required"
                shouldFocusOnMount
            >
                We weren’t able to verify that you’re human just yet. Please try again
                using the reCAPTCHA checkbox below.
            </Alert>
        );
    }

    if (errorType === 'recaptchaFailed') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Recaptcha Failed"
                shouldFocusOnMount
            >
                <p>
                    We couldn’t confirm that this request came from a real person. We’re
                    sorry for the hassle — this is to keep {process.env.SITE_NAME} safe.
                    You can try the steps below to resolve the issue.
                </p>
                <ul>
                    <li>Switch browsers or devices</li>
                    <li>Temporarily disable any VPN or proxy</li>
                    <li>Disable ad blocker or privacy browser extensions</li>
                    <li>
                        Check your device’s clock/timezone — incorrect system time can
                        occasionally cause problems
                    </li>
                    <li>
                        Try a different network (switch between Wi-Fi and mobile data) —
                        some networks trigger stricter checks.
                    </li>
                </ul>
            </Alert>
        );
    }

    if (errorType === 'invalidCode') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Invalid Code"
                shouldFocusOnMount
            >
                You supplied an incorrect verification code. Please double check the code
                and try again.
            </Alert>
        );
    }

    if (errorType === 'lockout') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Resend Limit Reached"
                shouldFocusOnMount
            >
                The email resend limit has been reached. You must wait 30 minutes before
                you can resend the email again. Verify that the email address you entered
                is correct and start over if it is wrong. Check your spam folder to see if
                the verification code email was incorrectly marked as junk.
            </Alert>
        );
    }

    if (errorType === 'unknownEmail') {
        return null; // handled by popup container
    }

    return (
        <Alert
            type="error"
            className={className}
            heading="Unknown Error"
            shouldFocusOnMount
        >
            Something went wrong while trying to create your account. Please try again in
            a moment. If the problem persists, try refreshing the page.
        </Alert>
    );
}
